import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Wrench, Plus, Pencil, Check, X, Loader2 } from "lucide-react";

function Row({ m, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(m.name);
  const [busy, setBusy] = useState(false);
  const patch = async (body) => {
    setBusy(true);
    try { await api.patch(`/erp/mechanics/${m.id}`, body); setEdit(false); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <li data-testid={`mechanic-row-${m.id}`} className="flex items-center gap-2 py-2">
      {edit ? (
        <>
          <Input data-testid={`mechanic-edit-input-${m.id}`} value={name} onChange={(e) => setName(e.target.value)} className="h-9 bg-zinc-900 border-zinc-800 text-base sm:text-sm" />
          <Button data-testid={`mechanic-save-${m.id}`} size="sm" disabled={busy} onClick={() => patch({ name })} className="h-9 bg-green-brand hover:bg-green-brandDark"><Check className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" onClick={() => { setEdit(false); setName(m.name); }} className="h-9 text-zinc-400"><X className="h-4 w-4" /></Button>
        </>
      ) : (
        <>
          <span className={`flex-1 text-sm ${m.active ? "text-zinc-100" : "text-zinc-500 line-through"}`}>{m.name}</span>
          <Button data-testid={`mechanic-edit-${m.id}`} size="sm" variant="ghost" onClick={() => setEdit(true)} className="h-8 text-zinc-400 hover:text-green-neon"><Pencil className="h-3.5 w-3.5" /></Button>
          <Button data-testid={`mechanic-toggle-${m.id}`} size="sm" variant="outline" disabled={busy} onClick={() => patch({ active: !m.active })} className="h-8 text-xs bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800">{m.active ? "Desativar" : "Ativar"}</Button>
        </>
      )}
    </li>
  );
}

export function MechanicsCard() {
  const [list, setList] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/erp/mechanics", { params: { all: true } }).then(({ data }) => setList(data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try { await api.post("/erp/mechanics", { name }); setName(""); load(); toast.success("Mecânico adicionado"); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="mechanics-card" className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-5 md:p-7">
      <div className="flex items-center gap-2 mb-3"><Wrench className="h-4 w-4 text-amber-brand" /><h2 className="font-display text-lg font-bold uppercase text-zinc-100">Mecânicos</h2></div>
      <p className="text-sm text-zinc-400 mb-3">Aparecem na lista "Mecânico responsável" da OS. Desativados somem da lista, mas continuam nas OS antigas.</p>
      <form onSubmit={add} className="flex gap-2">
        <Input data-testid="mechanic-new-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome do mecânico" className="bg-zinc-900 border-zinc-800 text-base sm:text-sm" />
        <Button data-testid="mechanic-add-btn" type="submit" disabled={busy || !name.trim()} className="bg-green-brand hover:bg-green-brandDark font-bold uppercase">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Adicionar</>}</Button>
      </form>
      {list === null ? <Loader2 className="h-4 w-4 animate-spin text-green-brand mt-3" /> : list.length === 0
        ? <p className="text-xs text-zinc-500 mt-3">Nenhum mecânico cadastrado.</p>
        : <ul className="divide-y divide-zinc-800 mt-2">{list.map((m) => <Row key={m.id} m={m} onChanged={load} />)}</ul>}
    </div>
  );
}
